import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

interface Reply { replyId: string; body: string; createdAt: string }
interface Ticket {
  supportTicketId: string;
  subject: string;
  description: string | null;
  priority: number;
  status: number;
  createdAt: string;
  replies: Reply[];
}

const STATUS_COLOR: Record<number, string> = { 1: 'blue', 2: 'orange', 3: 'green', 4: 'gray' };
const PRIO_COLOR: Record<number, string> = { 1: 'gray', 2: 'blue', 3: 'orange', 4: 'red' };

export function SupportPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [openTicket, setOpenTicket] = useState<Ticket | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['support', filter] as const,
    queryFn: async () =>
      (await apiClient.get<Ticket[]>('/api/support/tickets', {
        params: filter === 'all' ? {} : { status: filter },
      })).data,
  });

  const sLabel = (s: number) => t(`support.status.${s === 1 ? 'open' : s === 2 ? 'inProgress' : s === 3 ? 'resolved' : 'closed'}`);
  const pLabel = (p: number) => t(`support.prio.${p === 1 ? 'low' : p === 2 ? 'medium' : p === 3 ? 'high' : 'urgent'}`);

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.operations')} title={t('support.title')} subtitle={t('support.subtitle')} />
      <Group justify="space-between" wrap="wrap">
        <SegmentedControl value={filter} onChange={setFilter} data={[
          { value: 'all', label: t('support.all') },
          { value: '1', label: t('support.status.open') },
          { value: '2', label: t('support.status.inProgress') },
          { value: '3', label: t('support.status.resolved') },
          { value: '4', label: t('support.status.closed') },
        ]} />
        <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />} onClick={() => setCreateOpen(true)}>
          {t('support.new')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
          : (data ?? []).length === 0 ? <Text size="sm" c="dimmed" ta="center" py="lg">{t('support.empty')}</Text>
          : (
          <Table verticalSpacing="sm">
            <Table.Thead><Table.Tr>
              <Table.Th>{t('support.subject')}</Table.Th>
              <Table.Th>{t('support.priority')}</Table.Th>
              <Table.Th>{t('support.statusCol')}</Table.Th>
              <Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((tk) => (
                <Table.Tr key={tk.supportTicketId}>
                  <Table.Td>{tk.subject}</Table.Td>
                  <Table.Td><Badge variant="light" color={PRIO_COLOR[tk.priority]}>{pLabel(tk.priority)}</Badge></Table.Td>
                  <Table.Td><Badge variant="light" color={STATUS_COLOR[tk.status]}>{sLabel(tk.status)}</Badge></Table.Td>
                  <Table.Td><Group justify="flex-end">
                    <Button size="xs" variant="default" onClick={() => setOpenTicket(tk)}>{t('support.openTicket')}</Button>
                  </Group></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <CreateModal opened={createOpen} onClose={() => setCreateOpen(false)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['support'] })} />
      <TicketModal ticket={openTicket} onClose={() => setOpenTicket(null)}
        onChanged={() => void qc.invalidateQueries({ queryKey: ['support'] })} />
    </Stack>
  );
}

function CreateModal({ opened, onClose, onSaved }: { opened: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [desc, setDesc] = useState('');
  const [priority, setPriority] = useState('2');
  const save = useMutation({
    mutationFn: async () => apiClient.post('/api/support/tickets',
      { subject: subject.trim(), description: desc.trim() || null, customerId: null, priority: Number(priority) }),
    onSuccess: () => { setSubject(''); setDesc(''); onSaved(); onClose(); },
    onError: () => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });
  return (
    <Modal opened={opened} onClose={onClose} title={t('support.new')}>
      <Stack gap="sm">
        <TextInput label={t('support.subject')} value={subject} onChange={(e) => setSubject(e.currentTarget.value)} withAsterisk />
        <Select label={t('support.priority')} value={priority} onChange={(v) => setPriority(v ?? '2')} data={[
          { value: '1', label: t('support.prio.low') },
          { value: '2', label: t('support.prio.medium') },
          { value: '3', label: t('support.prio.high') },
          { value: '4', label: t('support.prio.urgent') },
        ]} />
        <Textarea label={t('support.description')} value={desc} onChange={(e) => setDesc(e.currentTarget.value)} autosize minRows={3} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={save.isPending} disabled={!subject.trim()} onClick={() => save.mutate()}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TicketModal({ ticket, onClose, onChanged }: { ticket: Ticket | null; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const [reply, setReply] = useState('');

  const addReply = useMutation({
    mutationFn: async () => apiClient.post(`/api/support/tickets/${ticket!.supportTicketId}/replies`, { body: reply.trim() }),
    onSuccess: () => { setReply(''); onChanged(); onClose(); },
  });
  const setStatus = useMutation({
    mutationFn: async (status: number) => apiClient.put(`/api/support/tickets/${ticket!.supportTicketId}`,
      { subject: ticket!.subject, description: ticket!.description, customerId: null, priority: ticket!.priority, status, assigneeUserId: null }),
    onSuccess: () => { onChanged(); onClose(); },
  });

  return (
    <Modal opened={ticket !== null} onClose={onClose} title={ticket?.subject} size="lg">
      <Stack gap="sm">
        {ticket?.description && <Text size="sm" c="dimmed">{ticket.description}</Text>}
        <Stack gap={6}>
          {(ticket?.replies ?? []).map((r) => (
            <Paper key={r.replyId} p="xs" withBorder><Text size="sm">{r.body}</Text></Paper>
          ))}
        </Stack>
        <Textarea label={t('support.reply')} value={reply} onChange={(e) => setReply(e.currentTarget.value)} autosize minRows={2} />
        <Group justify="space-between">
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={() => setStatus.mutate(3)}>{t('support.markResolved')}</Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => setStatus.mutate(4)}>{t('support.close')}</Button>
          </Group>
          <Button color="wheels" loading={addReply.isPending} disabled={!reply.trim()} onClick={() => addReply.mutate()}>
            {t('support.sendReply')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
